import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PagePanel,
  Box,
  TransferMoneyIcon,
  LoadingSpinner,
  IPagePanelFooter
} from 'edenred-ui';
import { useSelector, useDispatch } from 'react-redux';

import { useMenuDimensions } from '@epi-shared/hooks';
import { getCompanyAccounts } from '@epi-selectors/companyAccounts';
import { getCompanyBeneficariesRequest } from '@epi-api/companyBeneficiaries';
import { EmptyState, TransferPageHeader } from '@epi-shared/components';
import { OnboardingSupportInformation } from '@epi-components/OnboardingSupportInformation';
import { goToPage } from '@epi-actions/navigation';
import { routerPaths } from '@epi-constants/routerPaths';
import { loadCompanyAccounts, getUserCompany } from '@epi-actions/api';
import { loadCompanyAccounts as loadCompanyAccountsSelector } from '@epi-selectors/loading';
import OnboardingStepper from '@epi-components/OnboardingStepper/OnboardingStepper';

import { Topup } from './components';

export function LoadBenefit() {
  const { t } = useTranslation();
  const { isMobile } = useMenuDimensions();
  const dispatch = useDispatch();
  const [footer, setFooter] = useState<IPagePanelFooter>();
  const [isBeneficiariesLoaded, setIsBeneficiariesLoaded] =
    useState<boolean>(false);
  const [hasBeneficiaries, setHasBeneficiaries] = useState<boolean>(false);
  const isLoading = useSelector(loadCompanyAccountsSelector);
  const accounts = useSelector(getCompanyAccounts);

  const loadBeneficiaries = async (): Promise<void> => {
    const response = await getCompanyBeneficariesRequest();
    setIsBeneficiariesLoaded(true);
    setHasBeneficiaries(response.beneficiaries.length > 0);
  };

  const navigateToEmployees = (): void => {
    dispatch(goToPage(routerPaths.beneficiaries));
  };

  useEffect(() => {
    loadBeneficiaries();
    dispatch(loadCompanyAccounts());
    dispatch(getUserCompany());
  }, []);

  const renderPageContent = () => {
    if ((isLoading && accounts.length === 0) || !isBeneficiariesLoaded) {
      return <LoadingSpinner />;
    }

    if (!hasBeneficiaries) {
      return (
        <Box minHeight={590}>
          <EmptyState
            icon={<TransferMoneyIcon />}
            title={t('transfer_money.add_employees_title')}
            description={t('transfer_money.add_employees_description')}
            btnText={t('transfer_money.add_employees_btn')}
            btnOnClick={navigateToEmployees}
          />
        </Box>
      );
    }

    return <Topup updateFooter={setFooter} />;
  };

  return (
    <Box>
      <OnboardingStepper />
      <PagePanel
        title={t('containers.load_benefit.header')}
        footer={footer}
        headerContent={!isMobile && <TransferPageHeader />}
      >
        {renderPageContent()}
      </PagePanel>
      <OnboardingSupportInformation />
    </Box>
  );
}
